import { FunctionComponent, useLayoutEffect, useRef, useState } from 'react'
import { SiteRFPDocumentSummary } from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  rucksack,
} from '@black-bear-energy/rucksack'
import {
  isEntityUser,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import {
  getSiteRFPColumns,
  getSiteRFPTableWidth,
  MAX_SITE_RFPS_BEFORE_SCROLL,
  SITE_RFP_TABLE_BORDER_WIDTH,
  SITE_RFP_TABLE_FIRST_CELL_PADDING_LEFT,
  SITE_RFP_TABLE_MARGIN_LEFT,
} from '../helpers/DocumentExchange/tableConstants'
import { SiteRFPSection } from './SiteRFPSection'

export const SiteRFPTable: FunctionComponent<SiteRFPTableProps> = ({
  siteRFPDocumentSummaries,
  searchString,
  nestedSiteRFPTableProps,
}) => {
  /** If true, the site RFP table is nested within the customer RFP table. */
  const nested = nestedSiteRFPTableProps !== undefined
  const [siteRFPTableMaxHeight, setSiteRFPTableMaxHeight] = useState<
    number | null
  >(null)
  const siteRFPTableHeaderRef = useRef<HTMLTableSectionElement>(null)
  const siteRFPTableBodyRowRef = useRef<HTMLTableRowElement>(null)
  const siteRFPTableContainerRef = useRef<HTMLDivElement>(null)

  const user = useImpersonatingUserOrUser()
  const isClient = !!user && isEntityUser(user)
  const SITE_RFP_COLUMNS = getSiteRFPColumns(isClient)

  useLayoutEffect(() => {
    if (
      nested &&
      siteRFPTableHeaderRef.current &&
      siteRFPTableBodyRowRef.current &&
      siteRFPTableContainerRef.current
    ) {
      const headerHeight = siteRFPTableHeaderRef.current.offsetHeight
      const bodyRowHeight = siteRFPTableBodyRowRef.current.offsetHeight
      const baseHeight =
        headerHeight + MAX_SITE_RFPS_BEFORE_SCROLL * bodyRowHeight
      const horizontalScrollbarHeight =
        siteRFPTableContainerRef.current.offsetHeight -
        siteRFPTableContainerRef.current.clientHeight
      const maxHeight = baseHeight + horizontalScrollbarHeight
      setSiteRFPTableMaxHeight(maxHeight)
    }
  }, [nested, siteRFPDocumentSummaries])

  return (
    <Box
      sx={{
        borderColor: nested
          ? rucksack.palette.primary.outlinedBorder
          : rucksack.palette.divider,
        borderRadius: rucksack.shape.borderRadius,
        borderWidth: SITE_RFP_TABLE_BORDER_WIDTH,
        borderStyle: 'solid',
        overflowX: 'auto',
      }}
    >
      <TableContainer
        ref={siteRFPTableContainerRef}
        sx={{
          maxHeight:
            nested && siteRFPTableMaxHeight
              ? `${siteRFPTableMaxHeight}px`
              : undefined,
        }}
      >
        <Table
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: getSiteRFPTableWidth(
              siteRFPDocumentSummaries.length,
              isClient
            ),
            marginLeft: nested ? SITE_RFP_TABLE_MARGIN_LEFT : undefined,
          }}
        >
          <TableHead
            ref={siteRFPTableHeaderRef}
            sx={{ '& .MuiTableCell-head': { fontSize: '14px' } }}
          >
            <TableRow>
              {SITE_RFP_COLUMNS.map((column) => (
                <TableCell
                  key={column.title}
                  sx={{
                    width: `${column.width}%`,
                    // Keep the table's background color,
                    // not the default background color imposed by the sticky header.
                    backgroundColor: rucksack.palette.background.card,
                    '&:first-child': {
                      paddingLeft: nested
                        ? SITE_RFP_TABLE_FIRST_CELL_PADDING_LEFT
                        : undefined,
                    },
                  }}
                >
                  {column.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& > .MuiTableRow-root:last-child > .MuiTableCell-root': {
                borderBottom: nested ? undefined : 'none',
              },
            }}
          >
            {siteRFPDocumentSummaries.map((siteRFP) => (
              <SiteRFPSection
                key={siteRFP.siteRFPId}
                siteRFPDocumentSummary={siteRFP}
                nested={nested}
                customerRFPDashboardPath={
                  nestedSiteRFPTableProps?.customerRFPDashboardPath
                }
                searchString={searchString}
              />
            ))}
          </TableBody>
          {nested && (
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={SITE_RFP_COLUMNS.length}
                  sx={{ paddingY: 1, textAlign: 'center' }}
                >
                  <Button
                    variant="outlined"
                    href={nestedSiteRFPTableProps.customerRFPDashboardPath}
                    sx={{
                      '&:hover': {
                        color: rucksack.palette.primary.dark,
                      },
                    }}
                  >
                    view all sites
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  )
}

interface SiteRFPTableProps {
  siteRFPDocumentSummaries: SiteRFPDocumentSummary[]
  searchString?: string
  nestedSiteRFPTableProps?: NestedSiteRFPTableProps
}

interface NestedSiteRFPTableProps {
  open: boolean
  customerRFPDashboardPath: string
}
